import { PlusIcon } from "app/icons";
import { Box, Button, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AddOnTableRow } from "../../../types";
import { actions } from "../../../redux/slice";
import { ADD_ON_FORM_I18N_PREFIX, ADD_ON_TABLE_COLUMN_LABELS } from "../data";
import { AddOnTableColumnKey, AddOnTableConfig } from "../types";
import { useStyles } from "./styles";

type AddOnTableProps = {
  config: AddOnTableConfig;
  rows: AddOnTableRow[];
};

const resolveFieldValue = (fieldValue: string, t: (key: string) => string) =>
  fieldValue.startsWith("WORK_ORDER.") ? t(fieldValue) : fieldValue;

const getTableRowClassName = (
  columns: AddOnTableColumnKey[],
  styles: ReturnType<typeof useStyles>,
  variant: "head" | "body"
) => {
  const isCompact = columns.length === 2;

  if (variant === "head") {
    return isCompact ? styles.tableHeadRowCompact : styles.tableHeadRow;
  }

  return isCompact ? styles.tableBodyRowCompact : styles.tableBodyRow;
};

const AddOnTable = ({ config, rows }: AddOnTableProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const addRowLabelKey = `${ADD_ON_FORM_I18N_PREFIX}.TABLES.ADD_ROW`;

  const handleAddRow = () => {
    if (config.tableKey === "tyingStrip") {
      dispatch(
        actions.openAddOnTyingStripModal({ titleKey: config.modalTitleKey ?? "" })
      );
      return;
    }

    if (config.modalTitleKey) {
      dispatch(
        actions.openRowModal({
          tableKey: config.tableKey as "toolboxes" | "openBasket",
          titleKey: config.modalTitleKey,
        })
      );
      return;
    }

    dispatch(actions.addAddOnTableRow(config.tableKey));
  };

  const renderCellValue = (row: AddOnTableRow, column: AddOnTableColumnKey) => (
    <Typography className={styles.tableCellText}>
      {resolveFieldValue(row[column], t)}
    </Typography>
  );

  return (
    <Box className={styles.tableBlock}>
      <Box className={styles.tableHeaderRow}>
        <Typography className={styles.tableTitle}>{t(config.titleKey)}</Typography>
        <Button
          className={styles.addRowButton}
          variant="contained"
          color="primary"
          startIcon={<PlusIcon width={20} height={20} />}
          onClick={handleAddRow}
        >
          {t(addRowLabelKey)}
        </Button>
      </Box>

      <Box className={styles.table}>
        <Box className={getTableRowClassName(config.columns, styles, "head")}>
          <Typography className={styles.tableHeadCell}>#</Typography>
          {config.columns.map((column) => (
            <Typography key={column} className={styles.tableHeadCell}>
              {t(ADD_ON_TABLE_COLUMN_LABELS[column])}
            </Typography>
          ))}
        </Box>

        {rows.map((row, index) => (
          <Box
            key={row.id}
            className={getTableRowClassName(config.columns, styles, "body")}
          >
            <Box className={`${styles.tableCell} ${styles.tableIndexCell}`}>
              {index + 1}
            </Box>
            {config.columns.map((column) => (
              <Box key={`${row.id}-${column}`} className={styles.tableCell}>
                {renderCellValue(row, column)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AddOnTable;
